import React, { useMemo } from 'react';
import { Card, Form, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';

import {
  ExperimentAssignmentStrategy,
  getAssignmentStrategy,
  getExperimentStartAndEndTime,
  isTimeSliceShuffleStrategy,
  isViewMode,
} from '../../../constants';
import CustomTimeSlice from './custom-timeslice';
import AxaWarningAlert from '@/features/experiment/components/axa-warning-alert';
import DateTimePicker from '@/features/experiment/components/datetime-picker';
import { useExperimentContext } from '@/features/experiment/contexts/experiment-context';
import {
  isAxaIncompatibleStartTime,
  POST_STARTED_STATES,
} from '@/features/experiment/utils';

const NonCustomTimeSlice = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;
  const experimentAssignmentStrategy = getAssignmentStrategy(configurations);
  const [unixStart, unixEnd] = getExperimentStartAndEndTime(experiment);
  const formattedStartTime = unixStart ? dayjs.unix(unixStart) : undefined;
  const formattedEndTime = unixEnd ? dayjs.unix(unixEnd) : undefined;
  const isBalancedShuffleTimeSlice =
    experimentAssignmentStrategy ===
    ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE;
  const isSequentialTimeslice = isTimeSliceShuffleStrategy(
    experimentAssignmentStrategy,
  );
  const durationInDays = useMemo(() => {
    if (formattedStartTime && formattedEndTime) {
      return formattedEndTime.diff(formattedStartTime, 'days');
    }
    return undefined;
  }, [formattedStartTime, formattedEndTime]);

  const customStartTimeStatusAndError = useMemo(() => {
    if (readOnly || POST_STARTED_STATES.includes(experiment?.state))
      return {
        status: undefined,
        error: undefined,
      };

    if (!experiment?.startTime && experiment?.endTime) {
      return {
        status: 'error',
      };
    }

    if (experiment?.startTime?.isBefore(dayjs())) {
      return {
        status: 'error',
        error: new Error('Start time must be in the future'),
      };
    }

    return {
      status: undefined,
      error: undefined,
    };
  }, [experiment?.startTime, experiment?.endTime, experiment?.state]);

  const customEndTimeStatusAndError = useMemo(() => {
    if (readOnly)
      return {
        status: undefined,
        error: undefined,
      };

    if (!experiment?.endTime && experiment?.startTime) {
      return {
        status: 'error',
      };
    }

    if (experiment?.endTime?.isBefore(experiment?.startTime)) {
      return {
        status: 'error',
        error: new Error('End time must be after start time'),
      };
    }

    return {
      status: undefined,
      error: undefined,
    };
  }, [experiment?.startTime, experiment?.endTime, experiment?.state, readOnly]);

  // Only show AXA warning for sequential timeslice experiments
  const showAxaStartWarning =
    !readOnly &&
    isSequentialTimeslice &&
    isAxaIncompatibleStartTime(experiment?.startTime);

  if (isBalancedShuffleTimeSlice) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <Form.Item
          name={['startTime']}
          rules={[
            { required: true, message: '' },
            {
              validator(_, value) {
                // do not need to validate start time when experiment is
                // started, running, or in terminal states (completed/aborted/cancelled)
                if (POST_STARTED_STATES.includes(experiment?.state))
                  return Promise.resolve();

                if (dayjs(value).isBefore(dayjs())) {
                  return Promise.reject(
                    new Error('Start time must be in the future'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          style={{ marginBottom: 0 }}
          validateStatus={customStartTimeStatusAndError?.status as any}
          help={customStartTimeStatusAndError?.error?.message}
          shouldUpdate
        >
          <DateTimePicker id="startTime" disabled={readOnly} />
        </Form.Item>
        <Form.Item
          name={['endTime']}
          rules={[
            { required: true, message: '' },
            ({ getFieldValue }) => ({
              validator(_rule, value) {
                const startTime = getFieldValue('startTime');

                if (startTime && dayjs(value).isBefore(startTime)) {
                  return Promise.reject(
                    new Error('End time must be after start time'),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
          style={{ marginBottom: 0 }}
          shouldUpdate
          validateStatus={customEndTimeStatusAndError?.status as any}
          help={customEndTimeStatusAndError?.error?.message}
        >
          <DateTimePicker id="endTime" disabled={readOnly} />
        </Form.Item>
        <AxaWarningAlert show={showAxaStartWarning} type="startTime" />
      </div>
      {!!durationInDays && (
        <div style={{ paddingLeft: '8px' }}>
          <Typography.Text type="secondary">
            Duration: {durationInDays} days
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

const Duration = () => {
  return (
    <Card
      title={<Typography.Text strong>Duration (Dates)</Typography.Text>}
      styles={{
        body: {
          padding: 16,
        },
      }}
    >
      <NonCustomTimeSlice />
      <CustomTimeSlice />
    </Card>
  );
};

export default Duration;
